/*---------------------------------------------------------------------------------------------
 *  Copyright (c) by Solusek Systems. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import BaseCom from "../BaseCom";
import FormInput from "../FormInput";
import Card from "../Card";
import { withParamsAndNavigate } from "../WithParamsAndNavigate";
import { qreq } from "../../shared/qrequest";
import Breadcrumb from "../Breadcrumb";
import Address from "../Address";
import { SidebarGlobal } from "../_SidebarGlobal";
import MessageDialog from "../MessageDialog";
import Auth from "../../shared/Auth";
import Icon from "../Icon";
import AppActions from "../AppActions";
import React from "react";
import AccountPricingManager from "./AccountPricingManager";
import L from "../Lang";

class AccountEdit extends BaseCom {
    constructor(props) {
        super(props);
        this.state = {
            item: {
                defaultProviderID: 0,
                address: {},
                shippingAddress: {},
                billingAddress: {},
                productAttributes: []
            },
            loaded: false,
            edit: props.edit,
            providerList: [],
            accountTypeList: []
        };
        this.submitRef = React.createRef();
    }

    async componentDidMount() {
        await this.getSettings();

        this.loadProviders();
        this.loadAccountTypes(this.load);

        SidebarGlobal.setSubNav(<>
            <button type="button" className="btn btn-success" onClick={this.triggerSubmit}><Icon icon="faSave" /> <L>Save</L></button>
        </>);
    }

    componentWillUnmount() {
        SidebarGlobal.clearSubNav();
    }

    load = () => {
        if (!this.props.params.id) {
            this.setState({ loaded: true });
            return;
        }
        qreq.get('/api/account/get/' + this.props.params.id, j => {
            if (j.item) {
                this.setState({ item: j.item });
                this.accountTypeChange(j.item.accountTypeID, true);
            }
            else if (j.errorCode)
                this.alert(j.errorMessage);
            this.setState({ loaded: true });
        });
    }

    loadProviders = () => {
        qreq.post('/api/provider/list', {}, j => {
            if (j.list)
                this.setState({ providerList: j.list });
        });
    }

    loadAccountTypes = (then) => {
        qreq.get('/api/account/type/list', j => {
            if (j.list)
                this.setState({ accountTypeList: j.list });
            then?.();
        });
    }

    triggerSubmit = () => {
        this.submitRef.current.click();
    }

    save = (e) => {
        e?.preventDefault();
        this.setState({ saving: true });

        let item = { ...this.state.item };

        if (this.props.marketingPartner)
            item.isMarketingPartner = true;
        else if (this.props.facility)
            item.isFacility = true;
        else
            item.isGroup = true;

        item.productAttributes.forEach(i => {
            if (!i.price) i.price = 0;
            if (!i.upfrontFee) i.upfrontFee = 0;
        });

        qreq.post('/api/account/save', item, j => {
            if (j.errorCode === 0)
                this.goBack();
            else
                this.alert(j.errorMessage);
            this.setState({ saving: false });
        }, e => this.setState({ saving: false }));
    }

    productPriceChange = (product) => {
        let productAttributes = [...this.state.item.productAttributes];

        let attr = productAttributes.find(p => p.productID === product.id);
        if (attr) {
            if (product.customPrice)
                attr.price = parseFloat(product.customPrice);
            else
                attr.price = 0;
            attr.upfrontFee = parseFloat(product.upfrontFee);
            attr.isActive = product.isActive;
        }
        else
            productAttributes.push({
                productID: product.id,
                price: parseFloat(product.customPrice),
                upfrontFee: parseFloat(product.upfrontFee),
                isActive: product.isActive
            });

        this.setState({ item: { ...this.state.item, productAttributes: productAttributes }, productAttributes });
    }

    copyPrimaryToShipping = (address) => {
        this.setState({ item: { ...this.state.item, shippingAddress: { ...address, id: 0, masterID: null } } });
    }

    copyPrimaryToBilling = (address) => {
        this.setState({ item: { ...this.state.item, billingAddress: { ...address, id: 0, masterID: null } } });
    }

    change = () => {
        this.setState({ item: { ...this.state.item } });
    }

    accountTypeChange = (v, init) => {
        v = parseInt(v ?? '0');
        this.setState({ accountType: this.state.accountTypeList.find(at => at.id === v) });
        if (!init)
            this.change();
    }

    getAccountTypeOptions = () => {
        if (!Auth.hasRole('manager'))
            return this.state.accountTypeList.filter(a => a.level === 3);
        return this.state.accountTypeList;
    }

    render() {
        if (!this.state.loaded)
            return;
        const at = this.state.accountTypeList?.find(x => x.id === this.state.item?.accountTypeID);
        return (
            <div className="container-fluid">
                <h1><L>Accounts</L></h1>
                <Breadcrumb path={[['Accounts', '/account'], ['Edit']]} />
                <form onSubmit={this.save}>
                    <AppActions>
                        <button ref={this.submitRef} type="submit" className="btn btn-success btn-lg"><Icon icon="faSave" /> <L>Save</L></button>
                    </AppActions>
                    <div className="row">
                        <div className="col-md">
                            <Card title="Basic Information">
                                <FormInput model={this.state.item} name="name" label="Name" type="text" required={true} disabled={!((at?.level > 0 && Auth.hasPolicy('billing-distributor')) || Auth.hasPolicy('admin'))} />
                                {((at?.level > 0 && Auth.hasPolicy('billing-distributor')) || Auth.hasPolicy('admin')) && <FormInput model={this.state.item}
                                    name="accountTypeID"
                                    label="Type"
                                    type="select"
                                    required
                                    options={this.getAccountTypeOptions()}
                                    onChange={this.accountTypeChange}
                                />}
                                {(this.state.MODEL === 'WMS' || this.state.MODEL === 'LIS')
                                    && <FormInput model={this.state.item} name="defaultProviderID" label="Default Provider" type="select" options={this.state.providerList} />}
                            </Card>
                        </div>
                        {(this.state.MODEL === 'WMS' || this.state.MODEL === 'LIS') && <div className="col-md">
                            <Card title="More Information">
                                <div className="row">
                                    <div className="col">
                                        <FormInput model={this.state.item} name="npi" label="NPI" type="text" />
                                    </div>
                                    <div className="col">
                                        <FormInput model={this.state.item} name="tin" label="TIN" type="text" />
                                    </div>
                                    <div className="col">
                                        <FormInput model={this.state.item} name="ptan" label="PTAN" type="text" />
                                    </div>
                                </div>
                            </Card>
                        </div>}
                    </div>


                    <div className="row">
                        <div className="col-md-12">
                            <Card title="Contact Information">
                                <div className="row">
                                    <div className="col-md">
                                        <FormInput model={this.state.item} name="phone" label="Phone" type="text" />
                                    </div>
                                    <div className="col-md">
                                        <FormInput model={this.state.item} name="fax" label="Fax" type="text" />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md">
                                        <FormInput model={this.state.item} name="email" label="Email" type="email" />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md">
                                        <FormInput model={this.state.item} name="contactName" label="Contact Name" type="text" />
                                    </div>
                                    {(this.state.MODEL === 'WMS' || this.state.MODEL === 'LIS')
                                        && <div className="col-md">
                                            <FormInput model={this.state.item} name="medicareContactName" label="Medicare Contact Name" type="text" />
                                        </div>}
                                </div>
                            </Card>
                        </div>
                        {this.state.accountType?.hasAddress && <div className="col-md-6">
                            <Address
                                title="Contact Address"
                                item={this.state.item.address}
                                required={false}
                                onChange={this.change}
                            />
                        </div>}
                        {this.state.accountType?.hasBilling && <div className="col-md-6">
                            <Address
                                title="Billing Address"
                                item={this.state.item.billingAddress}
                                required={false}
                                primary={this.state.item.address}
                                onChange={this.change}
                                onCopy={this.copyPrimaryToBilling}
                            />
                        </div>}
                        {this.state.MODEL === 'MNF' && this.state.accountType?.hasShipping && <><div className="col-md-6">
                            <Address
                                title="Shipping Address"
                                item={this.state.item.shippingAddress}
                                required={false}
                                primary={this.state.item.address}
                                onChange={this.change}
                                onCopy={this.copyPrimaryToShipping}
                            />
                        </div>
                            <div className="col-md-6">
                                <Card title="Additional Shipping Properties">
                                    <FormInput model={this.state.item}
                                        name="fedexAccountNumber"
                                        label="FedEx Account Number"
                                        type="text"
                                        onChange={this.change}
                                        placeholder="Leave blank if not applicable"
                                    />
                                    <FormInput model={this.state.item}
                                        name="receiverName"
                                        label="Receiver Name"
                                        type="text"
                                        onChange={this.change}
                                        placeholder="Leave blank if not applicable"
                                    />
                                    <FormInput model={this.state.item}
                                        name="receiverPhone"
                                        label="Receiver Phone"
                                        type="text"
                                        onChange={this.change}
                                        placeholder="Leave blank if not applicable"
                                    />
                                </Card>
                            </div></>}
                    </div>

                    {((at?.level > 0 && Auth.hasPolicy('billing-distributor')) || Auth.hasPolicy('admin')) && this.state.MODEL === 'MNF' && <AccountPricingManager
                        account={this.state.item}
                        onPriceChange={(product) => this.productPriceChange(product)}
                    />}

                    <Card title="Terms and Conditions">
                        <FormInput
                            model={this.state.item}
                            name="terms"
                            label="Can be left blank for no terms and conditions."
                            labelAsPlaceholder
                            type="textarea"
                            onChange={this.change}
                        />
                    </Card>

                    <Card title="Flags">
                        {this.state.MODEL === 'MNF' && <FormInput
                            model={this.state.item}
                            name="canReceiveShipping"
                            label="Can Receive Shipping"
                            type="switch"
                            onChange={this.change}
                        />}
                        {Auth.hasPolicy('admin') && <FormInput
                            model={this.state.item}
                            name="isPaymentPortal"
                            label="Enable Payment Portal"
                            type="switch"
                            onChange={this.change}
                        />}
                    </Card>

                    {this.state.saving && <MessageDialog title="Saving" message="Processing..." />}
                </form>
            </div>
        );
    }
}

export default withParamsAndNavigate(AccountEdit);